<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SalesPartyDocumentEditor from './SalesPartyDocumentEditor.vue';
import SalesPropertyDocumentsUploadState from './SalesPropertyDocumentsUploadState.vue';

defineProps({
  documents: { type: Array, default: () => [] },
  pending: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  canUpload: { type: Boolean, default: false },
  documentTypes: { type: Array, default: () => [] },
  savingDocumentId: { type: [Number, String], default: null },
});

const emit = defineEmits([
  'download',
  'files',
  'remove',
  'saveTypes',
  'dismissPending',
]);
const { t } = useI18n();
const input = ref(null);
const dragging = ref(false);

const choose = () => input.value?.click();
const selectFiles = event => {
  emit('files', event.target.files);
  event.target.value = '';
};
const drop = event => {
  dragging.value = false;
  emit('files', event.dataTransfer.files);
};
const saveTypes = (document, types) => emit('saveTypes', document, types);
</script>

<template>
  <div class="w-full">
    <div
      v-if="canUpload"
      data-test="sales-property-documents-dropzone"
      class="mb-[30px] flex w-full cursor-pointer flex-col items-center justify-center rounded-[10px] border-2 border-dashed border-[#2DD2C5] bg-[#EDF8F7] px-[35px] py-[35px] text-center font-sans transition-colors"
      :class="
        dragging
          ? 'border-[#01988C] bg-[#EDF8F7]'
          : 'border-[#2DD2C5] bg-[#EDF8F7]'
      "
      @dragenter.prevent="dragging = true"
      @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false"
      @drop.prevent="drop"
      @click="choose"
    >
      <span
        class="i-lucide-file-plus mb-[10px] size-10 text-[#01988C]"
        aria-hidden="true"
        data-test="sales-property-documents-upload-icon"
      />
      <p
        class="mb-[28px] text-xl font-normal leading-6 text-[#464F53] max-[750px]:hidden"
        data-test="sales-property-documents-drop-text"
      >
        {{ t('SALES_PROPERTY.DOCUMENTS.DROP') }}
      </p>
      <p
        class="mb-[28px] text-xl font-normal leading-6 text-[#464F53] max-[750px]:hidden"
        data-test="sales-property-documents-or-text"
      >
        {{ t('SALES_PROPERTY.DOCUMENTS.OR') }}
      </p>
      <input
        ref="input"
        class="hidden"
        type="file"
        accept="application/pdf,.pdf"
        multiple
        @click.stop
        @change="selectFiles"
      />
      <LaisPrimaryButton
        class="!min-h-12 !w-[182px] !whitespace-nowrap !bg-[#14B8AA] !px-[15px] !py-[10px] !text-[14px] !font-bold hover:!bg-[#01988C]"
        @click.stop="choose"
      >
        {{ t('SALES_PROPERTY.DOCUMENTS.CHOOSE') }}
      </LaisPrimaryButton>
    </div>

    <SalesPropertyDocumentsUploadState
      :loading="loading"
      :pending="pending"
      @dismiss="emit('dismissPending', $event)"
    />

    <ul v-if="!loading && documents.length" class="grid gap-[15px]">
      <li
        v-for="document in documents"
        :key="document.id"
        class="min-w-0"
        data-test="sales-property-document-row"
      >
        <SalesPartyDocumentEditor
          compact
          :document="document"
          :options="documentTypes"
          :can-edit="canUpload && document.can_edit_types"
          :saving="savingDocumentId === document.id"
          :show-type-selector="documentTypes.length > 1"
          i18n-scope="SALES_PROPERTY"
          @save="saveTypes"
        >
          <template #fileActions>
            <button
              type="button"
              data-test="sales-property-document-file"
              class="mx-[15px] flex h-14 min-w-0 flex-1 items-center justify-between rounded-[10px] bg-lais-primary-50 px-[23px] py-4 text-left text-lais-primary disabled:cursor-not-allowed disabled:opacity-60 min-[751px]:max-w-[320px]"
              :disabled="!document.download_available"
              :aria-label="t('SALES_PROPERTY.DOCUMENTS.DOWNLOAD')"
              :title="t('SALES_PROPERTY.DOCUMENTS.DOWNLOAD')"
              @click="emit('download', document)"
            >
              <span class="truncate text-xl font-bold underline">
                {{ document.filename }}
              </span>
              <span class="i-lucide-check size-5 shrink-0" aria-hidden="true" />
            </button>
            <button
              v-if="document.can_delete"
              type="button"
              data-test="sales-property-document-remove"
              class="grid h-14 shrink-0 place-items-center rounded-[10px] bg-lais-danger px-[22px] py-[6px] text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:shadow-lais-focus"
              :aria-label="t('SALES_PROPERTY.DOCUMENTS.REMOVE')"
              :title="t('SALES_PROPERTY.DOCUMENTS.REMOVE')"
              @click="emit('remove', document)"
            >
              <span class="i-lucide-x size-5" aria-hidden="true" />
            </button>
          </template>
        </SalesPartyDocumentEditor>
      </li>
    </ul>
  </div>
</template>
